import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  ExpenseSchema,
  ExpenseCreateInputSchema,
  ExpenseUpdateInputSchema,
  type Expense,
  type ExpenseCreateInput,
  type ExpenseUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `expense` (frais engagé, org-wide en v0).
 *
 * Calque le patron `asset` : l'entrée est VALIDÉE par Zod
 * (`ExpenseCreateInputSchema`) AVANT toute écriture Firestore. Les champs
 * standard sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 *
 * Sécurité : org-wide en v0 (même doctrine qu'asset). Un frais est pourtant une
 * donnée personnelle (owner, valideur, comptabilité, manager du projet imputé) :
 * l'accès fin par rôle, sans lecture org-wide, viendra après v0.
 */

export type { ExpenseCreateInput };

/** Nom de la collection Firestore de la famille expense. */
export const EXPENSES_COLLECTION = 'expenses';

/**
 * Repository typé pour la famille `expense`.
 * Toute lecture est validée par `ExpenseSchema`, toute écriture par
 * `ExpenseCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface ExpenseRepository {
  create(input: ExpenseCreateInput): Promise<Expense>;
  list(options?: ListOptions): Promise<Expense[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Expense | null>;
  update(id: string, patch: ExpenseUpdateInput): Promise<Expense>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Expense>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Expense>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Expense>;
}

const expenseConverter = createConverter<Expense>((raw: DocumentData) => ExpenseSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createExpenseRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): ExpenseRepository {
  const col = collection(db, EXPENSES_COLLECTION).withConverter(expenseConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Expense>(db, col, EXPENSES_COLLECTION, getActorId);

  return {
    async create(input: ExpenseCreateInput): Promise<Expense> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = ExpenseCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.workerId !== undefined) optional['workerId'] = fields.workerId;
      if (fields.category !== undefined) optional['category'] = fields.category;
      if (fields.incurredAt !== undefined) optional['incurredAt'] = fields.incurredAt;
      if (fields.receiptDocumentId !== undefined) optional['receiptDocumentId'] = fields.receiptDocumentId;
      if (fields.rebillable !== undefined) optional['rebillable'] = fields.rebillable;
      if (fields.projectId !== undefined) optional['projectId'] = fields.projectId;
      if (fields.invoiceId !== undefined) optional['invoiceId'] = fields.invoiceId;
      if (fields.paymentId !== undefined) optional['paymentId'] = fields.paymentId;
      if (fields.status !== undefined) optional['status'] = fields.status;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        amount: fields.amount,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Expense);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return ExpenseSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Expense[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Expense | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: ExpenseUpdateInput): Promise<Expense> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = ExpenseUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, EXPENSES_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`expense ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
